"use client";
import { useCallback, useEffect, useMemo, useState } from "react";

// Intenta obtener una clave estable por usuario.
// Nota: si tu token NO es JWT, este método puede devolver "anon".
// Por eso, useTour acepta `userKeyOverride` para que el caller (ej. Home) entregue user.id/email.
function safeGetUserKey() {
  try {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("accessToken");

    if (!token) return "anon";

    // decode JWT sin librería (solo payload base64url)
    const parts = String(token).split(".");
    if (parts.length < 2) return "anon";

    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );

    return (
      payload?.sub ||
      payload?.id ||
      payload?.user_id ||
      payload?.email ||
      payload?.codigo_institucional ||
      "anon"
    );
  } catch {
    return "anon";
  }
}

export function useTour({ storageKey, enabled = true, userKeyOverride }) {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // ✅ Prioridad: override -> JWT -> "anon"
  const userKey = useMemo(() => {
    const k = userKeyOverride;
    if (k === null || k === undefined) return safeGetUserKey();
    const s = String(k).trim();
    return s.length ? s : safeGetUserKey();
  }, [userKeyOverride]);

  const finalKey = useMemo(() => `${storageKey}:${userKey}`, [storageKey, userKey]);

  useEffect(() => {
    if (!enabled) return;
    try {
      const seen = localStorage.getItem(finalKey) === "1";
      if (!seen) {
        setStepIndex(0);
        setOpen(true);
      }
    } catch {}
  }, [enabled, finalKey]);

  const close = useCallback(
    (markSeen = true) => {
      try {
        if (markSeen) localStorage.setItem(finalKey, "1");
      } catch {}
      setOpen(false);
    },
    [finalKey]
  );

  const next = useCallback((stepsLen) => {
    setStepIndex((i) => {
      const isLast = i >= stepsLen - 1;
      if (isLast) return i;
      return i + 1;
    });
  }, []);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const finishIfLast = useCallback(
    (stepsLen) => {
      setStepIndex((i) => {
        if (i >= stepsLen - 1) {
          // marca como visto y cierra
          try {
            localStorage.setItem(finalKey, "1");
          } catch {}
          setOpen(false);
          return i;
        }
        return i + 1;
      });
    },
    [finalKey]
  );

  return {
    open,
    stepIndex,
    setStepIndex,
    setOpen,
    close,
    next,
    back,
    finishIfLast,
    storageKey: finalKey,
    userKey,
  };
}
